import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChartPoint, VideoPostMarker } from '../../../shared/types/dashboard';
import ChartCard from '../ui/ChartCard';
import { getTooltipTransform } from '../ui/chartUtils';

const COLOR_LIKES = '#EC0452';
const COLOR_VIEWS = '#22C55E';
const COLOR_COMMENTS = '#94A3B8';
const COLOR_SHARES = '#F59E0B';

interface GrowthChartProps {
  data?: ChartPoint[];
  comparisonData?: ChartPoint[];
  videoPosts?: VideoPostMarker[];
  isLoading?: boolean;
  period?: string;
  username?: string;
}

const GrowthChart = ({ data, comparisonData, videoPosts = [], isLoading, period = '7d', username }: GrowthChartProps) => {
  const navigate = useNavigate();
  const [hoveredPoint, setHoveredPoint] = useState<number | null>(null);
  const [hoveredCompPoint, setHoveredCompPoint] = useState<number | null>(null);
  const [hoveredVideo, setHoveredVideo] = useState<number | null>(null);
  const [showVideoMarkers, setShowVideoMarkers] = useState(true);
  const [isHoveringCompLine, setIsHoveringCompLine] = useState(false);

  const periodLabel = period === '24h' ? 'Last 24 hours' :
    period === '7d' ? 'Last 7 days' :
    period === '1m' ? 'Last 30 days' :
    period === '3m' ? 'Last 90 days' :
    'Last 6 months';

  // Use real data or fallback to empty array
  const activeData = data || [];
  const compData = comparisonData || [];
  
  // Use .value from ChartPoint
  const dataPoints = activeData.map(d => d.value);
  const compPoints = compData.map(d => d.value);
  
  // If no data, show empty state or filler
  const allPoints = [...dataPoints, ...compPoints];
  const hasData = dataPoints.length > 1;
  const maxVal = allPoints.length > 0 ? Math.max(...allPoints) : 100;
  const minVal = allPoints.length > 0 ? Math.min(...allPoints) : 0;
  
  // Padding for labels
  const topPadding = 20;
  const bottomPadding = 20;
  const height = 200;
  const width = 1000; 

  const normalizeY = (val: number) => {
    if (maxVal === minVal) return height / 2;
    return (height - bottomPadding) - ((val - minVal) / (maxVal - minVal)) * (height - topPadding - bottomPadding);
  };

  const pointSpan = Math.max(1, dataPoints.length - 1);
  const points = hasData 
    ? dataPoints.map((val, index) => {
        const x = (index / pointSpan) * width;
        const y = normalizeY(val);
        return `${x},${y}`;
      }).join(' ')
    : `0,${height/2} ${width},${height/2}`;

  const compLinePoints = compPoints.length > 1
    ? compPoints.map((val, index) => {
        const x = (index / Math.max(1, compPoints.length - 1)) * width;
        const y = normalizeY(val);
        return `${x},${y}`;
      }).join(' ')
    : '';

  // Calculate video markers
  // ChartPoint uses .timestamp
  const timestamps = activeData.map(d => new Date(d.timestamp).getTime()).filter(ts => !isNaN(ts));
  const rangeStart = timestamps[0] ?? 0;
  const rangeEnd = timestamps[timestamps.length - 1] ?? rangeStart + 1;

  const videoMarkersRaw = (videoPosts || []).map(post => {
    if (!hasData || !post.posted_at) return null;

    let postTs: number;
    const rawAt = post.posted_at; // string in VideoPostMarker (ISO)
    postTs = new Date(rawAt).getTime();

    if (isNaN(postTs)) return null;

    const rangeDelta = rangeEnd === rangeStart ? 1 : rangeEnd - rangeStart;
    const relative = (postTs - rangeStart) / rangeDelta;
    
    // Only show if inside range
    if (relative < 0 || relative > 1) return null;

    const idx = Math.round(relative * (activeData.length - 1));
    const clampedY = dataPoints[idx] !== undefined ? normalizeY(dataPoints[idx]) : height/2;

    return {
      x: relative * width,
      y: clampedY,
      video: post,
      index: idx
    };
  }).filter((m): m is { x: number; y: number; video: VideoPostMarker; index: number } => m !== null);

  // Identify top performer of the visible period
  const topViewCount = Math.max(0, ...videoMarkersRaw.map(m => m.video.view_count || 0));
  const videoMarkers = videoMarkersRaw.map(m => ({
    ...m,
    isTop: topViewCount > 0 && m.video.view_count === topViewCount
  }));

  const growth = hasData ? dataPoints[dataPoints.length - 1] - dataPoints[0] : 0;

  const formatLargeNumber = (num: number) => {
    if (num >= 1000000) return (num / 1000000).toFixed(2) + ' M';
    if (num >= 10000) return (num / 1000).toFixed(2) + ' k';
    if (num >= 1000) return (num / 1000).toFixed(2) + ' k';
    return num.toString();
  };

  // Generate intelligent X-axis labels based on period
  const getXAxisLabels = () => {
    if (!hasData) return [];
    
    const labels = [];
    const count = activeData.length;
    
    // Always include start and end
    if (period === '24h') {
        // Hourly (format as 14:00)
        const step = Math.max(1, Math.floor(count / 6));
        for (let i = 0; i < count; i += step) {
            const d = new Date(activeData[i].timestamp || '');
            const text = isNaN(d.getTime()) ? '' : d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false });
            labels.push({ index: i, text }); 
        }
    } else if (period === '7d') {
        // Daily (format as Mon 20)
        const step = Math.max(1, Math.floor(count / 7));
        for (let i = 0; i < count; i += step) {
            const d = new Date(activeData[i].timestamp || '');
            const text = isNaN(d.getTime()) ? '' : d.toLocaleDateString([], { weekday: 'short', day: 'numeric' });
            labels.push({ index: i, text });
        }
    } else if (period === '1m') {
        // Daily (format as Jan 20)
        const step = Math.max(1, Math.floor(count / 7));
        for (let i = 0; i < count; i += step) {
            const d = new Date(activeData[i].timestamp || '');
            const text = isNaN(d.getTime()) ? '' : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
            labels.push({ index: i, text });
        }
    } else {
        // Weekly (3m, 6m)
        const step = Math.max(1, Math.floor(count / 8));
        for (let i = 0; i < count; i += step) {
            const d = new Date(activeData[i].timestamp || '');
            const text = isNaN(d.getTime()) ? '' : d.toLocaleDateString([], { month: 'short', day: 'numeric' });
            labels.push({ index: i, text });
        }
    }
    
    // Ensure last label is included if not already
    if (labels[labels.length - 1]?.index !== count - 1) {
        const lastD = new Date(activeData[count - 1].timestamp || '');
        const lastText = isNaN(lastD.getTime()) ? '' : (
            period === '24h' 
            ? lastD.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })
            : lastD.toLocaleDateString([], { month: 'short', day: 'numeric' })
        );
        labels.push({ index: count - 1, text: lastText });
    }
    
    return labels;
  };

  const xAxisLabels = getXAxisLabels();

  const handleChartMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    if (isHoveringCompLine || !hasData) return;
    
    const rect = e.currentTarget.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const relativeX = x / rect.width;
    
    // Find closest point
    const closestIndex = Math.round(relativeX * (dataPoints.length - 1));
    const clampedIndex = Math.max(0, Math.min(dataPoints.length - 1, closestIndex));
    
    setHoveredPoint(clampedIndex);
  };

  const handleChartMouseLeave = () => {
    if (!isHoveringCompLine) {
      setHoveredPoint(null);
    }
  };

  // Legend JSX
  const legend = (
    <>
      <div className="flex items-center gap-1.5">
        <div className="w-3 h-0.5 bg-[#38bdf8]"></div>
        <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Current</span>
      </div>
      {compLinePoints && (
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-0.5 border-t border-dashed border-slate-500"></div>
          <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Previous</span>
        </div>
      )}
      {videoMarkers.length > 0 && (
        <div 
          className={`flex items-center gap-1.5 cursor-pointer transition-opacity ${showVideoMarkers ? 'opacity-100' : 'opacity-50'}`}
          onClick={() => setShowVideoMarkers(!showVideoMarkers)}
          title="Toggle video markers"
        >
          <div className="w-2 h-2 border-2 border-[#ff00ff]"></div>
          <span className="text-[10px] text-slate-400 uppercase font-bold tracking-wider">Video Post</span>
        </div>
      )}
    </>
  );

  // Actions JSX (growth stats)
  const actions = (
    <div className="text-right">
      <span className={`text-2xl font-bold ${growth >= 0 ? 'text-emerald-400' : 'text-rose-400'}`}>
        {growth >= 0 ? '+' : ''}{growth.toLocaleString()}
      </span>
      <p className="text-xs text-slate-400 uppercase tracking-wide">followers</p>
    </div>
  );

  return (
    <ChartCard
      title="Follower Growth"
      subtitle={periodLabel}
      legend={legend}
      actions={actions}
      isLoading={isLoading}
      className="mb-8"
    >
      <div className="relative h-48 sm:h-64 w-full pt-2 pb-10">
        {hasData ? (
          <div className="relative h-full w-full">
            {/* Y-Axis Labels */}
            <div className="absolute left-0 top-0 h-full flex flex-col justify-between text-[10px] text-slate-500 pointer-events-none z-10 -ml-1 pr-2 border-r border-slate-800/30">
                <span>{formatLargeNumber(maxVal)}</span>
                <span>{formatLargeNumber(minVal + (maxVal - minVal) * 0.75)}</span>
                <span>{formatLargeNumber(minVal + (maxVal - minVal) * 0.5)}</span>
                <span>{formatLargeNumber(minVal + (maxVal - minVal) * 0.25)}</span>
                <span>{formatLargeNumber(minVal)}</span>
            </div>

            <div className="ml-8 h-full relative">
                {/* Hover detection area for entire chart */}
                <div 
                    className="absolute inset-0 cursor-crosshair z-10"
                    onMouseMove={handleChartMouseMove}
                    onMouseLeave={handleChartMouseLeave}
                />
                
                <svg viewBox={`0 0 ${width} ${height}`} className="w-full h-full overflow-visible" preserveAspectRatio="none">
                    {/* Vertical Grid Lines */}
                    {xAxisLabels.map((lbl, i) => (
                        <line 
                            key={`v-${i}`}
                            x1={(lbl.index / pointSpan) * width} 
                            y1={topPadding} 
                            x2={(lbl.index / pointSpan) * width} 
                            y2={height - bottomPadding} 
                            stroke="#334155" 
                            strokeWidth="0.5" 
                            opacity="0.2"
                            vectorEffect="non-scaling-stroke"
                        />
                    ))}

                    {/* Horizontal Grid lines */}
                    <line x1="0" y1={height - bottomPadding} x2={width} y2={height - bottomPadding} stroke="#334155" strokeWidth="0.5" vectorEffect="non-scaling-stroke" />
                    <line x1="0" y1={topPadding + (height - bottomPadding - topPadding) * 0.75} x2={width} y2={topPadding + (height - bottomPadding - topPadding) * 0.75} stroke="#334155" strokeWidth="0.5" opacity="0.2" strokeDasharray="4 4" vectorEffect="non-scaling-stroke"/>
                    <line x1="0" y1={(height - bottomPadding + topPadding) / 2} x2={width} y2={(height - bottomPadding + topPadding) / 2} stroke="#334155" strokeWidth="0.5" opacity="0.3" vectorEffect="non-scaling-stroke" />
                    <line x1="0" y1={topPadding + (height - bottomPadding - topPadding) * 0.25} x2={width} y2={topPadding + (height - bottomPadding - topPadding) * 0.25} stroke="#334155" strokeWidth="0.5" opacity="0.2" strokeDasharray="4 4" vectorEffect="non-scaling-stroke"/>
                    <line x1="0" y1={topPadding} x2={width} y2={topPadding} stroke="#334155" strokeWidth="0.5" strokeDasharray="4 4" vectorEffect="non-scaling-stroke"/>
                    
                    {/* Comparison Line (Previous Period) */}
                    {compLinePoints && (
                      <polyline
                        fill="none"
                        stroke="#64748b"
                        strokeWidth="1.5"
                        strokeDasharray="4 4"
                        points={compLinePoints}
                        opacity="0.5"
                        vectorEffect="non-scaling-stroke"
                      />
                    )}


                    {/* Area fill */}
                    <path
                    d={`M0,${height - bottomPadding} ${points} L${width},${height - bottomPadding} Z`}
                    fill="url(#gradient)"
                    opacity="0.15"
                    className="pointer-events-none"
                    />

                    
                    {/* Line */}
                    <polyline
                    fill="none"
                    stroke="#38bdf8"
                    strokeWidth="3"
                    points={points}
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    vectorEffect="non-scaling-stroke"
                    />

                    <defs>
                    <linearGradient id="gradient" x1="0%" y1="0%" x2="0%" y2="100%">
                        <stop offset="0%" stopColor="#38bdf8" />
                        <stop offset="100%" stopColor="#38bdf8" stopOpacity="0" />
                    </linearGradient>
                    </defs>

                </svg>

                {/* Interactive circles for comparison line - HTML overlay */}
                <div className="absolute inset-0 pointer-events-none overflow-hidden z-20">
                    {compPoints.map((val, index) => (
                        <div
                            key={`comp-interact-${index}`}
                            className="absolute pointer-events-auto cursor-pointer"
                            style={{
                                left: `${(index / Math.max(1, compPoints.length - 1)) * 100}%`,
                                top: `${(normalizeY(val) / height) * 100}%`,
                                width: '24px',
                                height: '24px',
                                transform: 'translate(-50%, -50%)'
                            }}
                            onMouseEnter={() => {
                                setIsHoveringCompLine(true);
                                setHoveredCompPoint(index);
                                setHoveredPoint(null);
                            }}
                            onMouseLeave={() => {
                                setIsHoveringCompLine(false);
                                setHoveredCompPoint(null);
                            }}
                        />
                    ))}
                </div>

                {/* Active Point Indicators - HTML Overlay (to preserve circular shape) */}
                <div className="absolute inset-0 pointer-events-none overflow-hidden">
                    {/* Comparison Line Dot */}
                    {hoveredCompPoint !== null && compPoints[hoveredCompPoint] !== undefined && (
                        <div
                            className="absolute rounded-full border-2 border-white bg-slate-500 shadow-sm"
                            style={{
                                left: `${(hoveredCompPoint / Math.max(1, compPoints.length - 1)) * 100}%`,
                                top: `${(normalizeY(compPoints[hoveredCompPoint]) / height) * 100}%`,
                                width: '10px',
                                height: '10px',
                                transform: 'translate(-50%, -50%)'
                            }}
                        />
                    )}

                    {/* Current Line Dot */}
                    {hoveredPoint !== null && dataPoints[hoveredPoint] !== undefined && (
                        <div
                            className="absolute rounded-full border-2 border-white bg-[#38bdf8] shadow-sm"
                            style={{
                                left: `${(hoveredPoint / (dataPoints.length - 1)) * 100}%`,
                                top: `${(normalizeY(dataPoints[hoveredPoint]) / height) * 100}%`,
                                width: '12px',
                                height: '12px',
                                transform: 'translate(-50%, -50%)'
                            }}
                        />
                    )}
                </div>

                {/* Video Post Markers - Non-scaling Overlay */}
                <div className="absolute inset-0 pointer-events-none">
                    {showVideoMarkers && videoMarkers.map((m, idx) => (
                      <div
                        key={`video-${idx}`}
                        className={`absolute pointer-events-auto cursor-pointer transition-all hover:scale-125 ${m.isTop ? 'z-30' : 'z-20'}`}
                        style={{
                          left: `${(m!.x / width) * 100}%`,
                          top: `${(m!.y / height) * 100}%`,
                          width: '14px',
                          height: '14px',
                          border: '3px solid #ff00ff',
                          backgroundColor: m.isTop ? '#ff00ff' : 'transparent',
                          transform: 'translate(-50%, -50%)',
                          boxSizing: 'border-box'
                        }}
                        onMouseEnter={() => setHoveredVideo(idx)}
                        onMouseLeave={() => setHoveredVideo(null)}
                        onClick={() => {
                          if (username && m.video.id) {
                            navigate(`/dashboard/${username}/video/${m.video.id}`);
                          }
                        }}
                      />
                    ))}
                </div>

                {/* Video Hover Tooltip */}
                {hoveredVideo !== null && videoMarkers[hoveredVideo] && (
                    <div 
                        className="absolute z-50 bg-slate-900/95 backdrop-blur-md border border-slate-700 p-3 rounded-xl shadow-2xl pointer-events-auto cursor-pointer hover:border-primary-500 transition-colors max-w-[320px] w-max"
                        style={{ 
                      left: `${(videoMarkers[hoveredVideo]!.x / width) * 100}%`, 
                            top: `${(videoMarkers[hoveredVideo]!.y / height) * 100}%`,
                            transform: 'translate(-50%, -115%)' 
                        }}
                        onClick={() => {
                          if (username && videoMarkers[hoveredVideo]?.video.id) {
                            navigate(`/dashboard/${username}/video/${videoMarkers[hoveredVideo]!.video.id}`);
                          }
                        }}
                    >
                        <div className="flex gap-3 mb-2">
                            {/* Video Thumbnail - 2 rows tall */}
                            {videoMarkers[hoveredVideo]!.video.cover_image_url ? (
                                <img 
                                    src={videoMarkers[hoveredVideo]!.video.cover_image_url} 
                                    alt="Video thumbnail"
                                    className="w-16 h-20 object-cover rounded-lg flex-shrink-0 bg-slate-800"
                                />
                            ) : (
                                <div className="w-16 h-20 bg-slate-800 rounded-lg flex-shrink-0 flex items-center justify-center">
                                    <svg className="w-8 h-8 text-slate-600" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor">
                                        <path strokeLinecap="round" strokeLinejoin="round" d="m15.75 10.5 4.72-4.72a.75.75 0 0 1 1.28.53v11.38a.75.75 0 0 1-1.28.53l-4.72-4.72M4.5 18.75h9a2.25 2.25 0 0 0 2.25-2.25v-9a2.25 2.25 0 0 0-2.25-2.25h-9A2.25 2.25 0 0 0 2.25 7.5v9a2.25 2.25 0 0 0 2.25 2.25Z" />
                                    </svg>
                                </div>
                            )}
                            
                            {/* Title and Date column */}
                            <div className="flex flex-col justify-between flex-1 min-w-0">
                                <p className="text-white text-xs font-bold line-clamp-2 leading-snug">
                                    {videoMarkers[hoveredVideo]!.video.title || 'Untitled Video'}
                                    {videoMarkers[hoveredVideo]!.isTop && <span className="ml-2 text-[10px] text-[#ff00ff] bg-[#ff00ff]/10 px-1.5 py-0.5 rounded border border-[#ff00ff]/20">TOP</span>}
                                </p>
                                <p className="text-slate-400 text-[10px] font-medium">
                                    {new Date(videoMarkers[hoveredVideo]!.video.posted_at).toLocaleDateString([], { month: 'short', day: 'numeric' })}, {new Date(videoMarkers[hoveredVideo]!.video.posted_at).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit', hour12: false })}
                                </p>
                            </div>
                        </div>
                        <div className="grid grid-cols-2 gap-x-2 gap-y-1">
                            <div className="flex items-center text-[10px] gap-0.5">
                                <span className="text-slate-400 font-medium">Views</span>
                                <span className="font-bold" style={{ color: COLOR_VIEWS }}>{videoMarkers[hoveredVideo]!.video.view_count.toLocaleString()}</span>
                            </div>
                            <div className="flex items-center text-[10px] gap-0.5">
                                <span className="text-slate-400 font-medium">Likes</span>
                                <span className="font-bold" style={{ color: COLOR_LIKES }}>{videoMarkers[hoveredVideo]!.video.like_count.toLocaleString()}</span>
                            </div>
                            <div className="flex items-center text-[10px] gap-0.5">
                                <span className="text-slate-400 font-medium">Comments</span>
                                <span className="font-bold" style={{ color: COLOR_COMMENTS }}>{(videoMarkers[hoveredVideo]!.video.comment_count || 0).toLocaleString()}</span>
                            </div>
                            <div className="flex items-center text-[10px] gap-0.5">
                                <span className="text-slate-400 font-medium">Shares</span>
                                <span className="font-bold" style={{ color: COLOR_SHARES }}>{(videoMarkers[hoveredVideo]!.video.share_count || 0).toLocaleString()}</span>
                            </div>
                        </div>
                    </div>
                )}

                {/* X-Axis Labels */}
                <div className="absolute left-0 bottom-[-24px] w-full flex justify-between text-[10px] text-slate-500">
                    {xAxisLabels.map((lbl, idx) => (
                        <div 
                            key={idx} 
                            className="absolute transform -translate-x-1/2 whitespace-nowrap"
                            style={{ left: `${(lbl.index / (activeData.length - 1)) * 100}%` }}
                        >
                            {lbl.text}
                        </div>
                    ))}
                </div>

                {/* Current Line Tooltip */}
                {hoveredPoint !== null && (
                    <div 
                        className="absolute bg-slate-800 text-white text-xs px-2 py-1.5 rounded border border-slate-700 shadow-2xl pointer-events-none z-40 transition-transform duration-75"
                        style={{
                            left: `${(hoveredPoint / (dataPoints.length - 1)) * 100}%`,
                            top: `${(normalizeY(dataPoints[hoveredPoint]) / height) * 100}%`,
                            transform: getTooltipTransform(hoveredPoint, dataPoints.length)
                        }}
                    >
                        <div className="font-bold whitespace-nowrap">{activeData[hoveredPoint].value.toLocaleString()}</div>
                        <div className="text-[10px] text-slate-400 whitespace-nowrap">
                            {new Date(activeData[hoveredPoint].timestamp || '').toLocaleString([], { 
                                month: 'short', 
                                day: 'numeric', 
                                hour: '2-digit', 
                                minute: '2-digit', 
                                hour12: false 
                            })}
                        </div>
                    </div>
                )}

                {/* Comparison Line Tooltip */}
                {hoveredCompPoint !== null && compData[hoveredCompPoint] && (
                    <div 
                        className="absolute bg-slate-700/95 text-white text-xs px-2 py-1.5 rounded border border-slate-600 shadow-2xl pointer-events-none z-40 transition-transform duration-75"
                        style={{
                            left: `${(hoveredCompPoint / Math.max(1, compPoints.length - 1)) * 100}%`,
                            top: `${(normalizeY(compPoints[hoveredCompPoint]) / height) * 100}%`,
                            transform: getTooltipTransform(hoveredCompPoint, compPoints.length)
                        }}
                    >
                        <div className="text-[9px] text-slate-400 whitespace-nowrap mb-0.5">Previous Period</div>
                        <div className="font-bold whitespace-nowrap">{compData[hoveredCompPoint].value.toLocaleString()}</div>
                        <div className="text-[10px] text-slate-300 whitespace-nowrap">
                            {new Date(compData[hoveredCompPoint].timestamp || '').toLocaleString([], { 
                                month: 'short', 
                                day: 'numeric', 
                                hour: '2-digit', 
                                minute: '2-digit', 
                                hour12: false 
                            })}
                        </div>
                    </div>
                )}
            </div>
          </div>
        ) : (
          <div className="flex items-center justify-center h-full text-slate-500">
            No growth data available for this period
          </div>
        )}
      </div>
    </ChartCard>
  );
};

export default GrowthChart;
